'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  nodeFill,
  NODE_FILL_DEEP,
  NODE_FILL_SOFT,
  NODE_R,
  RIM,
  RING_CENTRE,
  RING_CHORDS,
  RING_NODES,
  TIER_OPACITY,
  TIER_WIDTH,
  TIERS,
} from './figmaRing';
import {
  M_CANVAS_H,
  M_CANVAS_W,
  M_CENTRE,
  M_CHORD_TRANSFORM,
  M_DOT_R,
  M_NODES,
  M_RIM_R,
  M_RIM_WIDTH,
  M_STROKE_BOOST,
  M_TIER_WIDTH,
  M_TIERS,
} from './mobileGraph';
import { useSignalFlow, type SignalFlowRefs } from './useSignalFlow';
import styles from './PulseOmicsGraph.module.css';

/**
 * The drawing itself — everything in the section that holds a ref or runs a
 * frame. The head above it is static markup and is passed in as `children`
 * from the server component, so it costs no client JS.
 *
 * Two rings, one drawing. The narrow case keeps every node and every chord but
 * contracts the circle relative to its labels, which is the only way 16px type
 * survives a 390px column. CSS picks which is visible; `wide` picks which is
 * animated, so the hidden one is not burning frames.
 */

/**
 * Cropped to the drawing and centred on the ring rather than on its bounds:
 * the content reaches 434 either side of centre but only 354 above against
 * 378 below, and taking the larger for both keeps the circle in the middle.
 */
const PLOT_VIEWBOX = '208 161 884 770';

/* The design's own two blues: the soft one as the core, the deep one as the
   halo. A near-white core read as a streak laid over the drawing rather than
   as one of its own wires lighting up. */
const SIGNAL_CORE = '#8CA0FF';
const SIGNAL_GLOW = '#1539F5';

const INDEX_OF = new Map(RING_NODES.map((n, i) => [n.label, i]));
const CHORD_ENDS = RING_CHORDS.map((c) => ({
  from: INDEX_OF.get(c.from) ?? 0,
  to: INDEX_OF.get(c.to) ?? 0,
}));

/**
 * Which ring is on screen. Starts null so neither animates until the client
 * has measured — both are always rendered, which is what keeps the markup
 * identical between server and client.
 */
const useIsWide = () => {
  const [wide, setWide] = useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const read = () => setWide(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);
  return wide;
};

/**
 * The design's own opacities, held back a notch, so the lift a signal gives
 * them (see BASE_LIFT) has somewhere to go.
 */
const REST_DIM = 0.9;

const emptyRefs = (): SignalFlowRefs => ({
  base: [],
  core: [],
  glow: [],
  dots: [],
  hub: null,
  ends: CHORD_ENDS,
});

const PulseOmicsRing = ({ children }: { children?: ReactNode }) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const wide = useIsWide();
  const refs = useRef<SignalFlowRefs>(emptyRefs());
  const mRefs = useRef<SignalFlowRefs>(emptyRefs());

  useSignalFlow(refs, rootRef, { enabled: wide === true, dotR: NODE_R });
  useSignalFlow(mRefs, rootRef, {
    enabled: wide === false,
    dotR: M_DOT_R,
    softFilterId: 'po-signal-soft-m',
  });

  return (
    <section
      ref={rootRef}
      className={styles.section}
      role="region"
      aria-labelledby="pulseomics-heading"
    >
      {children}

      <figure className={styles.figure}>
        <svg
          className={styles.plot}
          viewBox={PLOT_VIEWBOX}
          role="img"
          aria-label="Nine body systems on a ring, each bound to the others through a single pulse"
        >
          <defs>
            <filter
              id="po-signal-soft"
              x="-25%"
              y="-25%"
              width="150%"
              height="150%"
            >
              <feGaussianBlur stdDeviation="2.4" />
            </filter>

            {/* One per chord, running end colour to end colour. gradientUnits
                is userSpaceOnUse so the ramp follows the chord's actual span
                rather than its bounding box, which for a curve that doubles
                back would otherwise put the two ends on the same stop. */}
            {RING_CHORDS.map((c, i) => {
              const a = RING_NODES.find((n) => n.label === c.from);
              const bNode = RING_NODES.find((n) => n.label === c.to);
              if (!a || !bNode) return null;
              return (
                <linearGradient
                  key={`g-${i}`}
                  id={`po-chord-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={a.dot.x}
                  y1={a.dot.y}
                  x2={bNode.dot.x}
                  y2={bNode.dot.y}
                >
                  <stop offset="0" stopColor={nodeFill(c.from)} />
                  <stop offset="1" stopColor={nodeFill(c.to)} />
                </linearGradient>
              );
            })}
          </defs>

          {/* The circle the nodes sit on, and the two arcs outside it that
              group them. Both go down before the chords: the bundle is the
              subject and the frame is what it hangs in, so nothing here
              should cross in front of a wire. */}
          <circle
            cx={RING_CENTRE.x}
            cy={RING_CENTRE.y}
            r={RIM.r}
            fill="none"
            stroke={RIM.stroke}
            strokeWidth={RIM.width}
          />
          {TIERS.map((t) => (
            <path
              key={t.label}
              d={t.d}
              fill="none"
              stroke={t.stroke}
              strokeOpacity={TIER_OPACITY}
              strokeWidth={TIER_WIDTH}
              strokeLinecap="round"
            />
          ))}

          {/* Base chords, each at the opacity, weight and colour the designer
              set for it — three depth tiers across two strokes. */}
          {RING_CHORDS.map((c, i) => (
            <path
              key={`base-${i}`}
              ref={(el) => {
                refs.current.base[i] = el;
              }}
              d={c.d}
              fill="none"
              stroke={`url(#po-chord-${i})`}
              strokeOpacity={c.opacity * REST_DIM}
              strokeWidth={c.width}
              strokeLinecap="round"
            />
          ))}

          {/* Signals: a soft halo under a bright core, both riding the same
              dash offset so they read as one travelling pulse. */}
          {RING_CHORDS.map((c, i) => (
            <g key={`sig-${i}`}>
              <path
                ref={(el) => {
                  refs.current.glow[i] = el;
                }}
                d={c.d}
                fill="none"
                stroke={SIGNAL_GLOW}
                strokeOpacity={0.2}
                strokeWidth={c.width * 6}
                strokeLinecap="round"
                opacity={0}
              />
              <path
                ref={(el) => {
                  refs.current.core[i] = el;
                }}
                d={c.d}
                fill="none"
                stroke={SIGNAL_CORE}
                strokeWidth={c.width * 1.9}
                strokeLinecap="round"
                opacity={0}
              />
            </g>
          ))}

          {RING_NODES.map((n, ni) => (
            <g key={n.label}>
              <circle
                ref={(el) => {
                  refs.current.dots[ni] = el;
                }}
                cx={n.dot.x}
                cy={n.dot.y}
                r={NODE_R}
                fill={n.deep ? NODE_FILL_DEEP : NODE_FILL_SOFT}
              />
              <text
                className={styles.nodeLabel}
                x={n.text.x}
                y={n.text.y}
                textAnchor={n.text.anchor}
                dominantBaseline="middle"
              >
                {n.label}
              </text>
            </g>
          ))}
        </svg>

        <svg
          className={styles.plotMobile}
          viewBox={`0 0 ${M_CANVAS_W} ${M_CANVAS_H}`}
          role="img"
          aria-label="Nine body systems on a ring, each bound to the others through a single pulse"
        >
          <defs>
            <filter
              id="po-signal-soft-m"
              x="-25%"
              y="-25%"
              width="150%"
              height="150%"
            >
              <feGaussianBlur stdDeviation="4" />
            </filter>

            {/* The narrow ring needs its own set: ids are document-global, and
                the chords here sit inside the scaling group, whose local space
                is still the design's — so the same userSpaceOnUse coordinates
                are correct on both. */}
            {RING_CHORDS.map((c, i) => {
              const a = RING_NODES.find((n) => n.label === c.from);
              const bNode = RING_NODES.find((n) => n.label === c.to);
              if (!a || !bNode) return null;
              return (
                <linearGradient
                  key={`gm-${i}`}
                  id={`po-chord-m-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={a.dot.x}
                  y1={a.dot.y}
                  x2={bNode.dot.x}
                  y2={bNode.dot.y}
                >
                  <stop offset="0" stopColor={nodeFill(c.from)} />
                  <stop offset="1" stopColor={nodeFill(c.to)} />
                </linearGradient>
              );
            })}
          </defs>

          {/* One transform carries the design's chords into this space; see
              M_CHORD_TRANSFORM. Widths are divided back out so scaling the
              group does not thin them into nothing. */}
          {/* Rim and tiers are drawn in this ring's own space, not inside
              the scaled group below — see M_RIM_R. */}
          <circle
            cx={M_CENTRE.x}
            cy={M_CENTRE.y}
            r={M_RIM_R}
            fill="none"
            stroke={RIM.stroke}
            strokeWidth={M_RIM_WIDTH}
          />
          {M_TIERS.map((t) => (
            <path
              key={t.label}
              d={t.d}
              fill="none"
              stroke={t.stroke}
              strokeOpacity={TIER_OPACITY}
              strokeWidth={M_TIER_WIDTH}
              strokeLinecap="round"
            />
          ))}

          <g transform={M_CHORD_TRANSFORM}>
            {RING_CHORDS.map((c, i) => (
              <path
                key={`m-base-${i}`}
                ref={(el) => {
                  mRefs.current.base[i] = el;
                }}
                d={c.d}
                fill="none"
                stroke={`url(#po-chord-m-${i})`}
                strokeOpacity={c.opacity * REST_DIM}
                strokeWidth={c.width * M_STROKE_BOOST}
                strokeLinecap="round"
              />
            ))}

            {RING_CHORDS.map((c, i) => (
              <g key={`m-sig-${i}`}>
                <path
                  ref={(el) => {
                    mRefs.current.glow[i] = el;
                  }}
                  d={c.d}
                  fill="none"
                  stroke={SIGNAL_GLOW}
                  strokeOpacity={0.2}
                  strokeWidth={c.width * M_STROKE_BOOST * 6}
                  strokeLinecap="round"
                  opacity={0}
                />
                <path
                  ref={(el) => {
                    mRefs.current.core[i] = el;
                  }}
                  d={c.d}
                  fill="none"
                  stroke={SIGNAL_CORE}
                  strokeWidth={c.width * M_STROKE_BOOST * 1.9}
                  strokeLinecap="round"
                  opacity={0}
                />
              </g>
            ))}
          </g>

          {M_NODES.map((n, ni) => (
            <g key={n.label}>
              <circle
                ref={(el) => {
                  mRefs.current.dots[ni] = el;
                }}
                cx={n.dot.x}
                cy={n.dot.y}
                r={M_DOT_R}
                fill={n.deep ? NODE_FILL_DEEP : NODE_FILL_SOFT}
              />
              <text
                className={styles.mobileNodeLabel}
                x={n.text.x}
                y={n.text.y}
                textAnchor={n.text.anchor}
                dominantBaseline="middle"
              >
                {n.label}
              </text>
            </g>
          ))}
        </svg>
      </figure>
    </section>
  );
};

export default PulseOmicsRing;
